import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { createBeamOrder, checkOrderStatus } from "@/lib/stripeApi";
import {
    Sparkles,
    Video,
    Zap,
    Clock,
    CheckCircle2,
    Star,
    Gift,
    Layers,
    Wand2,
    Mic,
    ImageIcon,
    X,
    QrCode,
    Loader2,
    Shield,
    BookOpen,
    Users,
    Quote,
    AlertTriangle,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const PROMO_PRICE = 1290;
const PROMO_CREDITS = 4000;
const PROMO_PACKAGE_ID = "promo-1290";
const CREDIT_VERIFICATION_MAX_ATTEMPTS = 8;
const REMAINING_SPOTS = 85;

// Testimonials data
const testimonials = [
    {
        name: "คุณสมชาย ว.",
        role: "YouTuber",
        avatar: "SC",
        content: "สั่ง 50 คลิปรอบเดียว เสร็จใน 2 ชม. ประหยัดเวลามากครับ คลิปสวยมาก ไม่มีลายน้ำด้วย!",
        rating: 5,
    },
    {
        name: "พี่แอน T.",
        role: "Content Creator",
        avatar: "AT",
        content: "Magic Button เจ๋งมาก กดปุ่มเดียวได้คลิปไวรัลเลย ไม่ต้องคิด prompt เอง",
        rating: 5,
    },
    {
        name: "น้องเบส K.",
        role: "TikToker",
        avatar: "BK",
        content: "คู่มือที่แถมมาช่วยได้มากเลยค่ะ สอนวิธีเขียน prompt ให้คลิปปังๆ",
        rating: 5,
    },
    {
        name: "คุณธนา S.",
        role: "เจ้าของธุรกิจ",
        avatar: "TS",
        content: "ใช้ทำคลิปโฆษณาสินค้า ลูกค้าชอบมาก ราคาคุ้มกว่าจ้างทีมงาน",
        rating: 5,
    },
    {
        name: "พี่ฟ้า M.",
        role: "Freelancer",
        avatar: "FM",
        content: "เครดิต 4,000 ใช้ได้นานมาก สร้างได้ทั้งรูป วิดีโอ และเสียง ครบ!",
        rating: 5,
    },
    {
        name: "คุณนิว P.",
        role: "นักเรียน",
        avatar: "NP",
        content: "ทำคลิปส่งอาจารย์ได้คะแนนดีมากค่ะ ใช้ง่าย สอนในกลุ่มก็เข้าใจ",
        rating: 5,
    },
];

const Promo1290 = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();

    const [showQRDialog, setShowQRDialog] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
    const [paymentStatus, setPaymentStatus] = useState<string>('pending');

    // Polling and credit verification refs
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const creditBaselineRef = useRef<number | null>(null);
    const creditVerificationAttemptsRef = useRef<number>(0);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Features list
    const features = [
        {
            icon: <Video className="h-6 w-6" />,
            title: t("Grok Video ไม่มีลายน้ำ", "Grok Video No Watermark"),
            desc: t("สร้างวิดีโอ AI คุณภาพสูงด้วย Grok Imagine จาก xAI", "Create high-quality AI videos with Grok Imagine from xAI"),
        },
        {
            icon: <Layers className="h-6 w-6" />,
            title: t("สั่งครั้งละ 50-100 คลิป", "Batch 50-100 clips at once"),
            desc: t("สั่งงานพร้อมกันหลายคำสั่ง ประหยัดเวลา", "Submit multiple jobs simultaneously, save time"),
        },
        {
            icon: <Clock className="h-6 w-6" />,
            title: t("เสร็จใน 2-3 ชั่วโมง", "Done in 2-3 hours"),
            desc: t("50 คลิปเสร็จภายในไม่กี่ชั่วโมง", "50 clips completed in just a few hours"),
        },
        {
            icon: <Wand2 className="h-6 w-6" />,
            title: t("Magic Button", "Magic Button"),
            desc: t("สุ่มสร้างวิดีโอไวรัลอัตโนมัติ", "Auto-generate viral videos randomly"),
        },
        {
            icon: <Mic className="h-6 w-6" />,
            title: t("Text-to-Speech", "Text-to-Speech"),
            desc: t("แปลงข้อความเป็นเสียงพากย์คุณภาพสูง", "Convert text to high-quality voice"),
        },
        {
            icon: <ImageIcon className="h-6 w-6" />,
            title: t("สร้างรูปภาพ AI", "AI Image Generation"),
            desc: t("สร้างรูปภาพจาก Prompt ด้วย AI ชั้นนำ", "Generate images from prompts with top AI"),
        },
    ];

    // What you can do with 4000 credits
    const creditUsage = [
        { label: t("วิดีโอ Grok (10 วินาที)", "Grok Videos (10s)"), count: "~130", icon: <Video className="h-5 w-5" /> },
        { label: t("วิดีโอ Grok (6 วินาที)", "Grok Videos (6s)"), count: "~200", icon: <Video className="h-5 w-5" /> },
        { label: t("รูปภาพ Grok AI", "Grok AI Images"), count: "800+", icon: <ImageIcon className="h-5 w-5" /> },
        { label: t("รูปภาพ AI อื่นๆ", "Other AI Images"), count: "1,000+", icon: <ImageIcon className="h-5 w-5" /> },
    ];

    // Capture credit baseline before payment
    const captureCreditBaseline = async () => {
        if (!user?.id) {
            creditBaselineRef.current = null;
            return;
        }

        try {
            const { data, error } = await supabase
                .from("profiles")
                .select("credits")
                .eq("user_id", user.id)
                .single();

            if (error || !data) {
                creditBaselineRef.current = null;
                return;
            }

            const credits = Number((data as any).credits);
            creditBaselineRef.current = Number.isFinite(credits) ? credits : null;
        } catch {
            creditBaselineRef.current = null;
        }
    };

    // Verify credits were added
    const verifyOrderCreditsAdded = async (): Promise<boolean> => {
        if (!user?.id || creditBaselineRef.current === null) {
            return true;
        }

        try {
            const { data, error } = await supabase
                .from("profiles")
                .select("credits")
                .eq("user_id", user.id)
                .single();

            if (error || !data) return false;

            const currentCredits = Number((data as any).credits);
            if (!Number.isFinite(currentCredits)) return false;

            return currentCredits >= (creditBaselineRef.current + PROMO_CREDITS);
        } catch {
            return false;
        }
    };

    // Start polling for payment status
    const startPaymentPolling = (orderId: string) => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = setInterval(async () => {
            try {
                const result = await checkOrderStatus(orderId);
                if (result.success) {
                    setPaymentStatus(result.status || 'pending');

                    if (result.status === 'completed') {
                        // Verify credits were actually added
                        const creditsAdded = await verifyOrderCreditsAdded();
                        if (!creditsAdded) {
                            creditVerificationAttemptsRef.current += 1;
                            setPaymentStatus('verifying');

                            if (creditVerificationAttemptsRef.current >= CREDIT_VERIFICATION_MAX_ATTEMPTS) {
                                clearInterval(pollingIntervalRef.current!);
                                pollingIntervalRef.current = null;
                                toast({
                                    variant: "destructive",
                                    title: t("การเติมเครดิตไม่สมบูรณ์", "Credit Update Incomplete"),
                                    description: t("ระบบรับชำระเงินแล้ว แต่ยังไม่สามารถยืนยันการเติมเครดิตได้ กรุณาติดต่อทีมสนับสนุน", "Payment was completed, but credits were not confirmed. Please contact support."),
                                });
                                return;
                            }

                            if (creditVerificationAttemptsRef.current === 1) {
                                toast({
                                    title: t("กำลังยืนยันเครดิต", "Verifying credits"),
                                    description: t("ชำระเงินสำเร็จแล้ว กำลังรอระบบอัปเดตเครดิต", "Payment is successful. Waiting for credits to update."),
                                });
                            }
                            return;
                        }

                        // Payment successful!
                        clearInterval(pollingIntervalRef.current!);
                        pollingIntervalRef.current = null;
                        toast({
                            title: t("🎉 ชำระเงินสำเร็จ!", "🎉 Payment Successful!"),
                            description: t("เครดิตถูกเติมเข้าบัญชีของคุณแล้ว กำลังไปหน้า Dashboard...", "Credits have been added. Redirecting to Dashboard..."),
                        });
                        // Redirect to dashboard after short delay
                        setTimeout(() => {
                            resetPaymentState();
                            setShowQRDialog(false);
                            navigate('/dashboard');
                        }, 2000);
                    } else if (result.status === 'failed' || result.status === 'cancelled') {
                        clearInterval(pollingIntervalRef.current!);
                        pollingIntervalRef.current = null;
                        toast({
                            variant: "destructive",
                            title: t("การชำระเงินล้มเหลว", "Payment Failed"),
                            description: t("กรุณาลองใหม่อีกครั้ง", "Please try again"),
                        });
                    } else if (result.expired) {
                        clearInterval(pollingIntervalRef.current!);
                        pollingIntervalRef.current = null;
                        toast({
                            variant: "destructive",
                            title: t("QR Code หมดอายุ", "QR Code Expired"),
                            description: t("กรุณาสร้าง QR ใหม่", "Please generate a new QR code"),
                        });
                        resetPaymentState();
                    }
                }
            } catch (error) {
                console.error("Error polling payment status:", error);
            }
        }, 3000); // Poll every 3 seconds
    };

    // Reset payment state
    const resetPaymentState = () => {
        setQrCodeUrl(null);
        setCurrentOrderId(null);
        setPaymentStatus('pending');
        creditBaselineRef.current = null;
        creditVerificationAttemptsRef.current = 0;
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    const handleBuyClick = async () => {
        if (!user) {
            // Not logged in - redirect to register with return URL
            toast({
                title: t("กรุณาสมัครสมาชิกก่อน", "Please register first"),
                description: t("ระบบจะพากลับมาหน้านี้หลังสมัครเสร็จ", "You'll be redirected back after registration"),
            });
            navigate("/register?redirect=/promo/1290");
            return;
        }

        // User is logged in - create Beam order
        setLoading(true);
        try {
            // Capture current credit balance
            await captureCreditBaseline();
            creditVerificationAttemptsRef.current = 0;

            // Create Beam order
            const result = await createBeamOrder({
                packageId: PROMO_PACKAGE_ID,
                credits: PROMO_CREDITS,
                price: PROMO_PRICE,
                bonus: 0,
                packageName: `Promo Package ${PROMO_CREDITS.toLocaleString()} Credits`,
                guestEmail: user.email || undefined,
            });

            if (result.success && result.qrCodeUrl) {
                setQrCodeUrl(result.qrCodeUrl);
                setCurrentOrderId(result.orderId!);
                setPaymentStatus('pending');
                setShowQRDialog(true);

                // Start polling for payment status
                startPaymentPolling(result.orderId!);
            } else {
                throw new Error(result.error || 'Failed to create payment');
            }
        } catch (error: any) {
            console.error("Error creating order:", error);
            toast({
                variant: "destructive",
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message || t("ไม่สามารถสร้างการชำระเงินได้", "Could not create payment"),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setShowQRDialog(false);
        resetPaymentState();
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-background via-purple-950/10 to-background">
            <Navbar />

            <main className="container mx-auto px-4 py-8 mt-16 md:mt-20">
                {/* Hero Section */}
                <div className="text-center max-w-4xl mx-auto mb-12">
                    <Badge className="mb-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 text-sm">
                        <Gift className="h-4 w-4 mr-1" />
                        {t("โปรโมชั่นพิเศษ", "Special Promotion")}
                    </Badge>

                    <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                        <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-orange-400 bg-clip-text text-transparent">
                            {t("แพ็คเกจสุดคุ้ม", "Best Value Package")}
                        </span>
                    </h1>

                    <p className="text-xl text-muted-foreground mb-4">
                        {t(
                            "สร้างวิดีโอ AI ด้วย Grok Imagine คุณภาพระดับมืออาชีพ สั่งได้ครั้งละหลายสิบคลิป!",
                            "Create professional AI videos with Grok Imagine, batch process dozens of clips!"
                        )}
                    </p>
                    <p className="text-lg text-purple-400 font-medium mb-6">
                        {t(
                            "100 คลิปทำแค่ 10 นาที สั่ง AI ทำ แล้วไปทำงานอื่นรอได้เลย",
                            "100 clips in just 10 minutes - Let AI work while you do other things"
                        )}
                    </p>

                    {/* Remaining Spots Alert */}
                    <div className="max-w-md mx-auto mb-6 animate-pulse">
                        <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-red-500/20 border border-red-500/40">
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                            <span className="text-red-500 font-bold">
                                {t(`เหลือเพียง ${REMAINING_SPOTS} สิทธิ์สุดท้าย!`, `Only ${REMAINING_SPOTS} spots remaining!`)}
                            </span>
                        </div>
                    </div>

                    {/* Price Card */}
                    <Card className="max-w-md mx-auto bg-gradient-to-br from-purple-600/20 to-pink-600/20 border-purple-500/30 overflow-hidden relative">
                        <div className="absolute top-0 right-0 bg-gradient-to-l from-yellow-500 to-orange-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                            {t("ประหยัด 40%", "Save 40%")}
                        </div>
                        <CardContent className="p-8">
                            <div className="text-center mb-2">
                                <span className="text-sm text-muted-foreground">{t("ราคา", "Price")}</span>
                            </div>
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <span className="text-5xl font-bold text-purple-400">฿{PROMO_PRICE.toLocaleString()}</span>
                                <span className="text-xl text-muted-foreground">{t("บาท", "THB")}</span>
                            </div>

                            <div className="text-center mb-4 p-3 rounded-lg bg-purple-500/20 border border-purple-500/30">
                                <div className="flex items-center justify-center gap-2">
                                    <Gift className="h-5 w-5 text-yellow-400" />
                                    <span className="text-yellow-400 font-bold">{t("ฟรี!", "FREE!")}</span>
                                </div>
                                <p className="text-white font-medium mt-1">
                                    {t("เครดิตสำหรับสร้าง VDO Grok Imagine", "Credits for Grok Imagine Video")}
                                </p>
                                <div className="flex items-center justify-center gap-2 mt-2">
                                    <Video className="h-6 w-6 text-purple-400" />
                                    <span className="text-3xl font-bold text-purple-400">100+</span>
                                    <span className="text-lg text-muted-foreground">{t("คลิป", "clips")}</span>
                                </div>
                            </div>

                            {/* Sora2 Discontinuation Notice */}
                            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-left">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-200">
                                        {t(
                                            "เนื่องจากมีการยกเลิกให้บริการ Sora2 ทางเราแนะนำให้ใช้ Grok ในการสร้างคลิปแทน 10 วินาที เอามาต่อกัน 2-3 คลิปครับ",
                                            "Due to Sora2 service discontinuation, we recommend using Grok to create 10-second clips and combine 2-3 clips together."
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Free Bonus */}
                            <div className="mb-6 p-3 rounded-lg bg-emerald-600 border border-emerald-500">
                                <div className="flex items-center gap-2 mb-1">
                                    <Gift className="h-4 w-4 text-white" />
                                    <span className="text-white font-bold text-sm">
                                        {t("แถมฟรี!", "FREE Bonus!")}
                                    </span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <BookOpen className="h-4 w-4 text-emerald-100 shrink-0 mt-0.5" />
                                    <p className="text-xs text-white font-medium">
                                        {t(
                                            "คู่มือการสร้าง Prompt + ตัวอย่างคลิปไวรัล สำหรับสั่ง Grok/Gemini ครั้งละมากๆ",
                                            "Prompt creation guide + viral clip examples for bulk Grok/Gemini generation"
                                        )}
                                    </p>
                                </div>
                            </div>

                            <Button
                                size="lg"
                                className="w-full h-14 text-lg gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/25"
                                onClick={handleBuyClick}
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <QrCode className="h-5 w-5" />
                                )}
                                {t("สมัครเลย - แสดง QR Code", "Subscribe Now - Show QR Code")}
                            </Button>

                            <p className="text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1">
                                <Shield className="h-3 w-3" />
                                {t("ชำระผ่าน PromptPay ปลอดภัย 100%", "Pay via PromptPay, 100% secure")}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Features Grid */}
                <div className="max-w-5xl mx-auto mb-12">
                    <h2 className="text-2xl font-bold text-center mb-8">
                        {t("ทำอะไรได้บ้าง?", "What can you do?")}
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {features.map((feature, i) => (
                            <Card key={i} variant="glass" className="border-white/10 hover:border-purple-500/30 transition-colors">
                                <CardContent className="p-5">
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400 shrink-0">
                                            {feature.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold mb-1">{feature.title}</h3>
                                            <p className="text-sm text-muted-foreground">{feature.desc}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Credit Usage */}
                <div className="max-w-3xl mx-auto mb-12">
                    <h2 className="text-2xl font-bold text-center mb-2">
                        {t("4,000 เครดิต ทำอะไรได้?", "What can 4,000 credits do?")}
                    </h2>
                    <p className="text-center text-muted-foreground mb-6">
                        {t("ตัวอย่างการใช้งาน (โดยประมาณ)", "Example usage (approximate)")}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {creditUsage.map((item, i) => (
                            <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/80 border border-slate-700">
                                <div className="p-2 rounded-lg bg-purple-600 text-white">
                                    {item.icon}
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm text-slate-300">{item.label}</div>
                                    <div className="text-2xl font-bold text-purple-400">{item.count}</div>
                                </div>
                                <CheckCircle2 className="h-5 w-5 text-purple-400" />
                            </div>
                        ))}
                    </div>

                    {/* Seedance 2.0 Coming Soon */}
                    <div className="mt-6 text-center">
                        <p className="text-sm text-purple-400 font-medium animate-pulse">
                            <Sparkles className="h-4 w-4 inline mr-1" />
                            {t("และเตรียมพบกับ Seedance 2.0 เร็วๆ นี้!", "And get ready for Seedance 2.0 coming soon!")}
                        </p>
                    </div>
                </div>

                {/* Highlights */}
                <div className="max-w-3xl mx-auto mb-12">
                    <Card variant="glass" className="border-yellow-500/20 bg-gradient-to-br from-yellow-500/5 to-orange-500/5">
                        <CardContent className="p-6">
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <Star className="h-5 w-5 text-yellow-400" />
                                {t("ทำไมต้องเลือกเรา?", "Why choose us?")}
                            </h3>
                            <ul className="space-y-3">
                                {[
                                    t("วิดีโอ Grok Imagine คุณภาพสูง ไม่มีลายน้ำ", "High-quality Grok Imagine videos, no watermark"),
                                    t("สั่งงานครั้งละ 50-100 คลิป ประหยัดเวลามหาศาล", "Batch 50-100 clips at once, save massive time"),
                                    t("Magic Button สุ่มสร้างวิดีโอไวรัลอัตโนมัติ", "Magic Button auto-generates viral videos"),
                                    t("รองรับทั้งภาพ วิดีโอ และเสียง ครบจบในที่เดียว", "Support images, videos, and audio all-in-one"),
                                    t("ไม่มีค่ารายเดือน เครดิตใช้ได้สูงสุด 1 ปี", "No monthly fees, credits valid for up to 1 year"),
                                    t("ทีมซัพพอร์ตพร้อมช่วยเหลือ", "Support team ready to help"),
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                </div>

                {/* Testimonials */}
                <div className="max-w-5xl mx-auto mb-12">
                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                            <Users className="h-6 w-6 text-purple-400" />
                            {t("รีวิวจากผู้ใช้จริง", "Real User Reviews")}
                        </h2>
                        <p className="text-muted-foreground">
                            {t("ดูว่าคนอื่นพูดถึงเราอย่างไร", "See what others say about us")}
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {testimonials.map((testimonial, i) => (
                            <Card key={i} variant="glass" className="border-white/10 hover:border-purple-500/20 transition-colors">
                                <CardContent className="p-5">
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                            {testimonial.avatar}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-sm">{testimonial.name}</h4>
                                            <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-0.5 mb-2">
                                        {[...Array(testimonial.rating)].map((_, j) => (
                                            <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                        ))}
                                    </div>
                                    <div className="relative">
                                        <Quote className="h-4 w-4 text-purple-400/50 absolute -top-1 -left-1" />
                                        <p className="text-sm text-muted-foreground pl-4">
                                            {testimonial.content}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="text-center max-w-md mx-auto">
                    {/* Remaining spots reminder */}
                    <div className="mb-4 flex items-center justify-center gap-2 text-red-500 font-bold animate-pulse">
                        <AlertTriangle className="h-4 w-4" />
                        {t(`เหลือ ${REMAINING_SPOTS} สิทธิ์สุดท้าย`, `${REMAINING_SPOTS} spots left`)}
                    </div>
                    <Button
                        size="lg"
                        className="w-full h-14 text-lg gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/25"
                        onClick={handleBuyClick}
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            <Zap className="h-5 w-5" />
                        )}
                        {t("รับ 4,000 เครดิต เพียง ฿1,290", "Get 4,000 credits for only ฿1,290")}
                    </Button>
                    <p className="text-sm text-muted-foreground mt-3">
                        {t("ชำระเงินง่ายผ่าน QR Code PromptPay", "Easy payment via QR Code PromptPay")}
                    </p>
                </div>
            </main>

            {/* QR Code Dialog */}
            <Dialog open={showQRDialog} onOpenChange={(open) => !open && handleCloseDialog()}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-center">
                            {t("สแกน QR Code เพื่อชำระเงิน", "Scan QR Code to Pay")}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-4 py-4">
                        {qrCodeUrl ? (
                            <div className="bg-white p-4 rounded-xl shadow-xl">
                                <img
                                    src={qrCodeUrl}
                                    alt="PromptPay QR Code"
                                    className="w-56 h-56 object-contain"
                                />
                            </div>
                        ) : (
                            <div className="w-64 h-64 flex items-center justify-center bg-muted rounded-lg">
                                <Loader2 className="h-8 w-8 animate-spin" />
                            </div>
                        )}

                        <div className="text-center">
                            <div className="text-3xl font-bold text-purple-400">฿{PROMO_PRICE.toLocaleString()}</div>
                            <div className="text-sm text-muted-foreground">
                                {t("รับ", "Get")} {PROMO_CREDITS.toLocaleString()} {t("เครดิต", "credits")}
                            </div>
                        </div>

                        {/* Payment Status */}
                        <div className={`w-full p-4 rounded-xl border-2 text-center ${
                            paymentStatus === 'completed' ? 'bg-green-500/10 border-green-500/50' :
                            paymentStatus === 'verifying' ? 'bg-amber-500/10 border-amber-500/50' :
                            paymentStatus === 'failed' ? 'bg-red-500/10 border-red-500/50' :
                            'bg-white/5 border-white/20'
                        }`}>
                            {paymentStatus === 'pending' && (
                                <>
                                    <Loader2 className="h-8 w-8 animate-spin text-cyan-500 mx-auto mb-2" />
                                    <p className="font-bold">{t("รอการชำระเงิน", "Waiting for payment")}</p>
                                    <p className="text-xs text-muted-foreground">{t("กรุณาสแกน QR และชำระเงิน", "Please scan QR and complete payment")}</p>
                                </>
                            )}
                            {paymentStatus === 'completed' && (
                                <>
                                    <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                                    <p className="font-bold text-green-500">{t("ชำระเงินสำเร็จ!", "Payment Successful!")}</p>
                                    <p className="text-xs text-green-500/70">{t("กำลังไปหน้า Dashboard...", "Redirecting to Dashboard...")}</p>
                                </>
                            )}
                            {paymentStatus === 'verifying' && (
                                <>
                                    <Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto mb-2" />
                                    <p className="font-bold text-amber-500">{t("กำลังยืนยันเครดิต", "Verifying Credits")}</p>
                                    <p className="text-xs text-amber-500/70">{t("ชำระเงินสำเร็จแล้ว กำลังรอระบบอัปเดตเครดิต", "Payment completed, waiting for credits to update")}</p>
                                </>
                            )}
                            {paymentStatus === 'failed' && (
                                <>
                                    <X className="h-8 w-8 text-red-500 mx-auto mb-2" />
                                    <p className="font-bold text-red-500">{t("การชำระเงินล้มเหลว", "Payment Failed")}</p>
                                    <p className="text-xs text-red-500/70">{t("กรุณาลองใหม่อีกครั้ง", "Please try again")}</p>
                                </>
                            )}
                        </div>

                        <p className="text-xs text-muted-foreground text-center">
                            {t("QR Code หมดอายุใน 15 นาที", "QR expires in 15 minutes")}
                        </p>

                        {paymentStatus === 'pending' && (
                            <Button
                                variant="outline"
                                onClick={handleCloseDialog}
                            >
                                {t("ยกเลิก", "Cancel")}
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Promo1290;
